import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

// Import existing root reducer (temporary - will migrate to slices)
import rootReducer from "./rootReducer";
import { baseApi } from "./api/baseApi";
import { publicApi } from "./api/publicApi";
import { setTokenListener } from "../utils/tokenProvider";
import { setTokens, IdTokenClaims } from "./slices/authSlice";

/**
 * Redux Persist Configuration
 *
 * Persistence strategy:
 * - Key: 'skypoint-studio-root' for localStorage key
 * - Version: 1 (increment when state shape changes)
 * - Whitelist: Only persist important state that should survive refreshes
 * - Blacklist: Exclude temporary UI state and large data that can be re-fetched
 */
const persistConfig = {
  key: "skypoint-studio-root",
  version: 1,
  storage,
  // Whitelist: Persist these reducers
  whitelist: [
    "tenantsReducer", // Keep selected tenant
    "notificationReducer", // Keep notifications
    "userPreferencesReducer", // Keep user preferences (timezone, dashboard settings)
  ],
  // Blacklist: Don't persist these reducers
  blacklist: [
    "authReducer", // Just trigger actions, no state to persist
    "tableReducer", // Temporary table state
    "dataflowReducer", // Large data, can be re-fetched
    "dashboardReducer", // Dashboard can reload
    "instanceHistoryReducer", // Large historical data
    "api", // RTK Query cache - will be refetched
  ],
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Configure Redux Store with Redux Toolkit
 *
 * Benefits of configureStore vs createStore:
 * - Includes redux-thunk by default
 * - Enables Redux DevTools automatically
 * - Includes middleware for immutability and serialization checks in development
 * - Simplifies store setup with good defaults
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types from redux-persist
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(baseApi.middleware, publicApi.middleware),
  // DevTools enabled automatically in development, disabled in production
  devTools: process.env.NODE_ENV !== "production",
});

// Create persistor for PersistGate
export const persistor = persistStore(store);

/**
 * Mirror every token the provider acquires into `authReducer`.
 *
 * The API clients no longer READ from here — they acquire per request — but a
 * large number of components still gate their queries on
 * `skip: !accessToken`, and `currentUser`/`useAuth` read the claims. Left
 * unmirrored, those would keep reporting whatever boot happened to store, so a
 * token refreshed mid-session would never reach them.
 *
 * Registered here rather than inside `tokenProvider` because the provider must
 * not import the store: the store imports `baseApi`, `baseApi` imports the
 * provider, and importing back would close that cycle.
 */
setTokenListener((accessToken, idTokenClaims) => {
  store.dispatch(
    setTokens({
      accessToken,
      idTokenClaims: idTokenClaims as IdTokenClaims | undefined,
    }),
  );
});

/**
 * Type Exports for TypeScript Support
 *
 * Usage:
 * ```typescript
 * import { useAppSelector, useAppDispatch } from './store/hooks';
 *
 * const myState = useAppSelector((state) => state.someReducer.someValue);
 * const dispatch = useAppDispatch();
 * ```
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
